import { Component, ElementRef, EventEmitter, Input, Output, Type, ViewChild, inject } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { Button } from 'primeng/button';
import { CascadeSelect } from 'primeng/cascadeselect';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from "primeng/datepicker";
import { Fieldset } from 'primeng/fieldset';
import { InputMask } from 'primeng/inputmask';
import { InputNumber } from 'primeng/inputnumber';
import { Listbox } from 'primeng/listbox';
import { MultiSelect } from 'primeng/multiselect';
import { Password } from 'primeng/password';
import { RadioButton } from 'primeng/radiobutton';
import { Rating } from 'primeng/rating';
import { Select } from "primeng/select";
import { Tabs } from "primeng/tabs";
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Objects } from "src/modules/utils/shared/objects";
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Strings } from "src/modules/utils/shared/strings";
import { Utils } from 'src/modules/utils/shared/utils';
import { InsertionPointDirective } from '../../directives/insertion-point.directive';
import { TableData } from '../../models/table-data.model';
import { TableRowEvent } from "../../models/table-row-event.model";
import { TableRow } from '../../models/table-row.model';
import { FrontendField } from '../../netgen/FrontendField';
import { FrontendFieldType } from '../../netgen/FrontendFieldType';
import { FrontendForm } from '../../netgen/FrontendForm';
import { ColorEditorComponent } from '../color-editor/color-editor.component';
import { SmFileUploadComponent } from '../sm-file-upload/sm-file-upload.component';
import { SmImageUploadComponent } from '../sm-image-upload/sm-image-upload.component';
import { SmTableComponent } from '../sm-table/sm-table.component';

@Component({
    selector: 'app-sm-form-field-internal',
    templateUrl: './sm-form-field-internal.component.html',
    styleUrls: ['./sm-form-field-internal.component.scss'],
    standalone: false
})
export class SmFormFieldInternalComponent {

    _FrontendFieldType = FrontendFieldType;
    _Strings = Strings;
    _Utils = Utils;

    app = inject(MainAppService);
    elementRef = inject(ElementRef);
 
    identity = (option): any => option; 
    
    errorMessage = "";
    fixedPos = false;
    tableData: TableData;
    tableDataIsForValue: any[];

    @Input()
    form: FrontendForm;
    @Input()
    fluentWidth: string = null;
    @Input()
    fullHeight = false;

    mainRadioButtonField: FrontendField;

    @Output()
    onClick = new EventEmitter<FrontendField>();

    private _field: FrontendField = null;
    private valueBeforeEdit: any;
    internalTimeValue: string;

    autoCompleteSuggestions = [];

    pageChildren: FrontendField[][][] = [];

    _mustRefresh = this.mustRefresh.bind(this);

    @ViewChild(InsertionPointDirective, {static: true}) insertionPoint: InsertionPointDirective;

    @ViewChild("componentFieldSet") componentFieldSet: Fieldset;
    @ViewChild("componentInput") componentInput: any;
    @ViewChild("componentPassword") componentPassword: Password;
    @ViewChild("componentInputNumber") componentInputNumber: InputNumber;
    @ViewChild("componentRating") componentRating: Rating;
    @ViewChild("componentInputMask") componentInputMask: InputMask;
    @ViewChild("componentMultiSelect") componentMultiSelect: MultiSelect;
    @ViewChild("componentAutoComplete") componentAutoComplete: AutoComplete;
    @ViewChild("componentSelect") componentDropdown: Select;
    @ViewChild("componentCascadeSelect") componentCascadeSelect: CascadeSelect;
    @ViewChild("componentListBox") componentListBox: Listbox;
    @ViewChild("componentTextArea") componentTextArea: any;
    @ViewChild("componentDatePicker") componentDatePicker: DatePicker;
    @ViewChild("componentCheckBox") componentCheckBox: Checkbox;
    @ViewChild("componentRadioButton") componentRadioButton: RadioButton;
    @ViewChild("componentColorEditor") componentColorEditor: ColorEditorComponent;
    @ViewChild("componentButton") componentButton: Button;
    @ViewChild("componentButtonAsLink") componentButtonAsLink: any;
    @ViewChild("componentFileUpload") componentFileUpload: SmFileUploadComponent;
    @ViewChild("componentImageUpload") componentImageUpload: SmImageUploadComponent;
    @ViewChild("componentTable") componentTable: SmTableComponent;
    @ViewChild("componentTabView") componentTabView: Tabs;
    
    _addTableRow = this.addTableRow.bind(this);
    _setTableRows = this.setTableRows.bind(this);

    ngAfterViewInit(): void {
        let ne = this.componentAutoComplete?.el?.nativeElement;
        if (ne != null) {
            //Bei einem AutoComplete wollen wir nicht, dass die Tab Taste zum Dropdown-Pfeil-Button springt, sondern direkt zum nächsten Control
            ne.querySelector('.p-autocomplete-dropdown')?.setAttribute('tabindex', '-1');
        }
        ne = this.componentDropdown?.el?.nativeElement;
        if (ne != null) {
            //Andernfalls wird das Drücken von Tab von p-select verschluckt und das Tabbing-Verhalten ist sehr merkwürdig
            ne.addEventListener('keydown', (e: KeyboardEvent) => {
                this._field.tableCellEditor?.handleKeyboardEvent(e);
            }, { capture: true });
        }
        this.field.guiComponent = this.getGuiComponent();
    }

    ngDoCheck(_): void {
        let errors = this.field?.getErrors(true, true, false) ?? [];
        this.errorMessage = Utils.isNoe(errors) ? null : Arrays.itemsToString(errors, "\n", error => error.message);
        this.fixedPos = this.field?.isFixedPosition();
        if (this.field?.type == FrontendFieldType.table) {
            if (this.tableData == null || this.tableDataIsForValue != this.field.value || this.tableDataIsForValue != null && this._field.value != null && this.tableDataIsForValue.length != this._field.value.length || this.tableData.columns?.length != this.field.tableColumns?.length) {
                this.tableData = this.field.value != null ? new TableData(this.field.tableColumns, (this.field.value as any[]).map(row => new TableRow("", row, row as OrdinaryObject))) : null;
                this.tableDataIsForValue = this.field.value;
            }
        }
        else {
            this.tableData = null;
        }
        if (this.field.tableSelectedRow != this.field.tableSelectedRowRequest) {
            this.tableSetSelectedRowIndex(this.field.tableSelectedRowRequest);
        }
        if (this.field.type == FrontendFieldType.radioButton) {
            this.mainRadioButtonField = this.form.getAllFields().find(f => f.radioButtonGroup == this.field.radioButtonGroup && f.radioButtonValue == 0);    
        }   
        if (this.field?.type == FrontendFieldType.time) {
            this.internalTimeValue = Dates.toTimeString(this.field.value as number);
        }

        if (this.field.requestFocus) {
            this.field.requestFocus = false;
            this.focus();
        }
        
    }

    @Input()
    set field(value: FrontendField) {
        this._field = value;

        if (this.field.type == FrontendFieldType.custom) {
            this._field.component = this.app.createComponent(this.insertionPoint, this._field.componentType as Type<any>);
            this._field.component.value = this._field.value;
            this._field.component.tableCellEditor = this._field.tableCellEditor;
           /* this._field.component.valueChange.subscribe(newValue => {
                this.valueChanged(true);
            });
          */  this._field.component.setContext(this._field.context);
        }
        this.pageChildren = this.field?.type == FrontendFieldType.pageControl ? this.field.children.map(page => page.getGroupedChildren()) : [];
    }

    get field(): FrontendField {
        return this._field;
    }

    buttonClick(event: MouseEvent): void {
        //Aus unbekannten Gründen wird dieser Eventhandler auch aufgerufen, wenn in einem beliebigen Formfeld Enter gedrückt wird.
        //Dann sind screenX und screenY = 0. Solche "Klicks" werden ignoriert.
        if (event.screenX === 0 && event.screenY === 0) {
            return;
        }
        if (this._field.onClick != null) {
            this._field.onClick(event);
        }
    }

    keyPress(event: KeyboardEvent): void {
        if (this.form.submitOnEnter && event.key == "Enter") {
            let button = this.form.getSubmitButton();
            if (button?.onClick != null) {
                button?.onClick(null);
                return;
            }
        }

        if (this._field.onKeyPress != null) {
            this._field.onKeyPress(event);
        }
    }

    mustRefresh(): boolean {
        if (this._field == null) {
            return false;
        }
        let result = this._field.tableMustRefresh;
        this._field.tableMustRefresh = false;
        return result;
    }

    valueChanged(byUser = true): void {
        let f = this.mainRadioButtonField ?? this._field;
        
        if (f.type == FrontendFieldType.dropdown && f.tableCellEditor != null) {
            f.tableCellEditor.toNextCell();
        }

        if (f.type == FrontendFieldType.bool && f.checkBoxTriState) {
            f.value = f.value === null ? true : f.value === true ? false : null;
        }

        if (f.value != null && (f.type == FrontendFieldType.decimal || f.type == FrontendFieldType.int) && this.field.allowOnlySpinnerStepValues) {
            let corrected = Utils.roundToDecimal(f.value as number, f.spinnerStep);
            if (f.value != corrected) {
                GuiUtils.angularTimer(() => {
                    f.value = corrected;
                });
            }
        }

        //Diese Zeile benötigen wir, weil das AutoComplete beim Selektieren eines Items zuerst ein ValueChanged mit Wert null abfeuert, und danach
        //ein zweites mit dem ausgewählten Wert. Diesen temporären Zwischenstand wollen wir nicht weitergeben
        if (f.value == null && f.type == FrontendFieldType.dropdown && f.dropdownAutoComplete && this.componentAutoComplete?.overlayVisible) {
            return;
        }

        if (f?.type == FrontendFieldType.time) {
            f.value = Dates.fromTimeString(this.internalTimeValue);
        }
        f.valueWasChanged(byUser);
    }

    addTableRow(): void {
        if (this._field.onBeforeAddRow != null) {
            this._field.onBeforeAddRow();
        }
        this._field.value = [...(this._field.value as object[]), this._field.generateEmptyTableItem()];
        this.valueChanged();
    }

    removeTableRow(evt: TableRowEvent): void {
        this._field.value = Arrays.withoutIndex(this._field.value as any[], evt.rowIndex);
        this.valueChanged();
    }

    setTableRows(rows: TableRow[]): void {
        this._field.value = rows.map(row => row.values);
        this.valueChanged();
    }

    deleteTableRow(rowIndex: number): void {
        (this._field.value as object[]).splice(rowIndex, 1);
        this.valueChanged();
        //Das wird benötigt, um die Change Detection zu triggern. Sonst stimmen Breiten von Header und Zeilen nicht überein
        this._field.value = [...(this._field.value as object[])];
    }

    async editTableRow(rowIndex: number): Promise<void> {
        let form = new FrontendForm(this._field.tableColumns.map(col => col.toFrontendField()));
        form.fill((this._field.value as object[])[rowIndex]);
        await this.app.dialog.form(form, "Zeile editieren");
    }

    tableSelectRow(row: TableRow): void {
        this.field.tableSelectedRow = this.tableData.rows.findIndex(row2 => row2 == row);
        this.field.tableSelectedRowRequest = this.field.tableSelectedRow;
    }

    tableSetSelectedRowIndex(index: number): void {
        this.field.tableSelectedRow = index;
        this.field.tableSelectedRowRequest = index;
        if (this.componentTable != null) {
            this.componentTable.setSelectedRowIndex(index);
        }
    }

    tableEditInit(): void {
        this.valueBeforeEdit = Objects.cloneDeep(this.field.value);
    }

    tableEditComplete(): void {
        if (!Objects.equalsDeep(this.valueBeforeEdit, this.field.value)) {
            this._field.valueWasChanged(true);
        }
    }

    focus(): void {
        let c = this.getGuiComponent();
        if (c != null) {
            GuiUtils.angularTimer(() => {
                c.nativeElement?.focus();
                //für Multiselekt;
                c.containerViewChild?.nativeElement?.querySelector('input')?.focus();
            });
        }
    }

    thisComponentAsTableEditorInited(): void {
        this.componentDropdown?.show(true);
    }

    async autoComplete(event: any): Promise<void> {
        if (this._field.onAutoComplete != null) {
            this.autoCompleteSuggestions = await this._field.onAutoComplete(event.query as string);
        }
        else {
            this.autoCompleteSuggestions = this.field.listItems.filter(li => li.label != null && Strings.includesCi(li.label, event.query as string));
            if (this._field.dropdownAutoCompleteMaxItems != null) {
                this.autoCompleteSuggestions = Arrays.subList(this.autoCompleteSuggestions, 0, this._field.dropdownAutoCompleteMaxItems);
            }
        }
    }

    getColorPalette(): string[] {
        return this.field.predefinedColors.map(c => Utils.toColorHexString(c));
    }

    colorChanged(color: string): void {
        this.field.value = Utils.toColorNumber(color);
    }

    getStyle(style: OrdinaryObject): OrdinaryObject {
        return  { ... style, ...this.field.style };
    }

    getGuiComponent(): any {
        return this.componentFieldSet
            ?? this.componentInput
            ?? this.componentPassword
            ?? this.componentInputNumber
            ?? this.componentRating
            ?? this.componentInputMask
            ?? this.componentMultiSelect
            ?? this.componentAutoComplete
            ?? this.componentDropdown
            ?? this.componentCascadeSelect
            ?? this.componentListBox
            ?? this.componentTextArea
            ?? this.componentDatePicker
            ?? this.componentCheckBox
            ?? this.componentRadioButton
            ?? this.componentColorEditor
            ?? this.componentButton
            ?? this.componentButtonAsLink
            ?? this.componentFileUpload
            ?? this.componentImageUpload
            ?? this.componentTable
            ?? this.componentTabView
            ?? null;
    }

}